import { Keyboard, X } from 'lucide-react'
import { useEffect, useRef } from 'react'

interface GraphKeyboardHelpProps {
  open: boolean
  onClose: () => void
}

const shortcuts = [
  ['+', 'Zoom in'],
  ['-', 'Zoom out'],
  ['0', 'Fit graph'],
  ['Esc', 'Close help'],
] as const

export function GraphKeyboardHelp({ open, onClose }: GraphKeyboardHelpProps) {
  const dialogRef = useRef<HTMLElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    closeButtonRef.current?.focus()
  }, [open])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }

    if (event.key !== 'Tab') return

    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ) ?? [],
    ).filter((el) => !el.hasAttribute('disabled'))

    if (!focusable.length) {
      event.preventDefault()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement

    if (event.shiftKey && active === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  if (!open) return null

  return (
    <div className="absolute inset-0 z-30 flex items-start justify-center bg-black/30 px-4 py-16 backdrop-blur-sm">
      <section
        ref={dialogRef}
        className="w-full max-w-sm rounded-lg border border-white/[0.08] bg-surface-1 p-4 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Graph keyboard shortcuts"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Keyboard size={14} className="text-neutral-400" />
            <h2 className="text-sm font-semibold text-neutral-200">Keyboard</h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-md text-neutral-500 hover:bg-surface-2 hover:text-neutral-200"
            aria-label="Close keyboard help"
          >
            <X size={14} />
          </button>
        </div>
        <div className="flex flex-col gap-2">
          {shortcuts.map(([key, label]) => (
            <div key={key} className="flex items-center justify-between gap-3">
              <span className="text-xs text-neutral-400">{label}</span>
              <kbd className="rounded border border-white/[0.08] bg-surface-2 px-2 py-1 text-[11px] text-neutral-300">
                {key}
              </kbd>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
